/* Store locator: Leaflet map with OpenStreetMap tiles, local search on city and postal code,
   sorting by distance with the browser location. Leaflet (assets/leaflet.js) loads before this file. */
(function () {
  'use strict';

  if (window.customElements.get('store-locator')) return;

  const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  const ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-bijdragers';
  const PIN_SVG =
    '<svg viewBox="0 0 32 42" width="32" height="42" aria-hidden="true" focusable="false">' +
    '<path d="M16 1C7.7 1 1 7.6 1 15.8 1 26.4 16 41 16 41s15-14.6 15-25.2C31 7.6 24.3 1 16 1z" fill="#ffbf19" stroke="#101010" stroke-width="2"/>' +
    '<circle cx="16" cy="15.5" r="5.5" fill="#101010"/></svg>';

  const TEXT = {
    count: (n) => (n === 1 ? '1 salon' : n + ' salons'),
    found: (n, q) => (n === 1 ? '1 salon gevonden voor "' + q + '"' : n + ' salons gevonden voor "' + q + '"'),
    none: 'Geen salons gevonden. Probeer een andere plaats of postcode.',
    locating: 'Locatie bepalen...',
    locateLabel: 'Gebruik mijn locatie',
    sorted: 'Gesorteerd op afstand vanaf je locatie.',
    noGeo: 'Je browser ondersteunt geen locatiebepaling. Zoek op plaats of postcode.',
    denied: 'Je hebt geen toestemming gegeven voor je locatie. Zoek in plaats daarvan op plaats of postcode.',
    unavailable: 'Je locatie kon niet worden bepaald. Zoek op plaats of postcode.',
    timeout: 'Het bepalen van je locatie duurde te lang. Probeer het opnieuw of zoek op plaats of postcode.',
    noMap: 'De kaart kon niet worden geladen. De lijst met salons werkt wel.',
  };

  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function normalise(value) {
    return (value == null ? '' : String(value))
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function compact(value) {
    return normalise(value).replace(/\s/g, '');
  }

  function toNumber(value) {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  function distanceKm(a, b) {
    const r = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const la1 = (a.lat * Math.PI) / 180;
    const la2 = (b.lat * Math.PI) / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
    return 2 * r * Math.asin(Math.sqrt(h));
  }

  function formatKm(km) {
    const text = km < 10 ? km.toFixed(1) : String(Math.round(km));
    return text.replace('.', ',') + ' km';
  }

  function el(tag, text, className) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.setup();
    }

    disconnectedCallback() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    setup() {
      this.form = this.querySelector('[data-store-locator-form]');
      this.search = this.querySelector('[data-store-locator-search]');
      this.locateButton = this.querySelector('[data-store-locator-locate]');
      this.status = this.querySelector('[data-store-locator-status]');
      this.mapEl = this.querySelector('[data-store-locator-map]');
      this.list = this.querySelector('[data-store-locator-list]');
      this.emptyEl = this.querySelector('[data-store-locator-empty]');
      this.query = '';
      this.userPosition = null;
      this.markers = new Map();

      this.salons = this.readSalons();
      this.items = new Map();
      this.querySelectorAll('[data-store-locator-item]').forEach((item) => {
        this.items.set(item.dataset.handle, item);
        const show = item.querySelector('[data-store-locator-show]');
        if (show) show.addEventListener('click', () => this.showSalon(item.dataset.handle));
      });

      if (this.form) this.form.addEventListener('submit', (event) => event.preventDefault());
      if (this.search) this.search.addEventListener('input', () => this.applyFilter(this.search.value));
      if (this.locateButton) this.locateButton.addEventListener('click', () => this.locate());

      this.initMap();
      this.applyFilter('');
    }

    readSalons() {
      const script = this.querySelector('[data-store-locator-salons]');
      if (!script) return [];
      try {
        const data = JSON.parse(script.textContent || '[]');
        return data.map((salon) => ({
          handle: salon.handle,
          name: salon.name || '',
          street: salon.street || '',
          postal_code: salon.postal_code || '',
          city: salon.city || '',
          country: salon.country || 'NL',
          phone: salon.phone || '',
          website: salon.website || '',
          note: salon.note || '',
          lat: toNumber(salon.lat),
          lng: toNumber(salon.lng),
        }));
      } catch (error) {
        return [];
      }
    }

    get visibleSalons() {
      return this.salons.filter((salon) => this.matches(salon));
    }

    matches(salon) {
      if (!this.query) return true;
      const q = compact(this.query);
      return compact(salon.city).includes(q) || compact(salon.postal_code).startsWith(q);
    }

    initMap() {
      if (!this.mapEl) return;
      if (!window.L) {
        this.setStatus(TEXT.noMap);
        return;
      }
      const L = window.L;
      const lat = toNumber(this.dataset.centerLat);
      const lng = toNumber(this.dataset.centerLng);
      const zoom = toNumber(this.dataset.zoom);

      this.map = L.map(this.mapEl, {
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
      });
      L.tileLayer(TILE_URL, { maxZoom: 19, attribution: ATTRIBUTION }).addTo(this.map);
      this.map.setView([lat == null ? 51.8 : lat, lng == null ? 4.9 : lng], zoom == null ? 7 : zoom);

      this.icon = L.divIcon({
        className: 'store-locator__pin',
        html: PIN_SVG,
        iconSize: [32, 42],
        iconAnchor: [16, 41],
        popupAnchor: [0, -36],
      });

      this.salons.forEach((salon) => {
        if (salon.lat == null || salon.lng == null) return;
        const marker = L.marker([salon.lat, salon.lng], {
          icon: this.icon,
          title: salon.name,
          alt: salon.name,
          keyboard: true,
        });
        marker.bindPopup(this.popupContent(salon));
        marker.on('click keypress', () => this.setCurrent(salon.handle, true));
        this.markers.set(salon.handle, marker);
      });

      this.onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.map && this.map.invalidateSize(), 150);
      };
      window.addEventListener('resize', this.onResize);
      window.setTimeout(() => this.map && this.map.invalidateSize(), 50);
    }

    popupContent(salon) {
      const wrap = document.createElement('div');
      const name = el('p');
      name.appendChild(el('strong', salon.name));
      wrap.appendChild(name);
      const lines = [];
      if (salon.street) lines.push(salon.street);
      let place = [salon.postal_code, salon.city].filter(Boolean).join(' ');
      if (salon.country && salon.country !== 'NL') place += ' (' + salon.country + ')';
      if (place) lines.push(place);
      if (lines.length) wrap.appendChild(el('p', lines.join(', ')));
      if (salon.note) wrap.appendChild(el('p', salon.note));
      return wrap;
    }

    applyFilter(value) {
      this.query = normalise(value);
      const visible = this.visibleSalons;
      const visibleHandles = new Set(visible.map((salon) => salon.handle));

      this.items.forEach((item, handle) => {
        item.hidden = !visibleHandles.has(handle);
      });
      if (this.emptyEl) this.emptyEl.hidden = visible.length > 0;
      if (this.list) this.list.hidden = visible.length === 0;

      this.updateMarkers(visibleHandles);

      if (visible.length === 0) {
        this.setStatus(TEXT.none);
      } else if (this.query) {
        this.setStatus(TEXT.found(visible.length, value.trim()));
      } else {
        this.setStatus(this.userPosition ? TEXT.sorted : TEXT.count(visible.length));
      }
    }

    updateMarkers(visibleHandles) {
      if (!this.map) return;
      const bounds = [];
      this.markers.forEach((marker, handle) => {
        if (visibleHandles.has(handle)) {
          marker.addTo(this.map);
          bounds.push(marker.getLatLng());
        } else {
          marker.closePopup();
          this.map.removeLayer(marker);
        }
      });
      if (this.userMarker) bounds.push(this.userMarker.getLatLng());
      this.fit(bounds);
    }

    fit(points) {
      if (!this.map) return;
      const options = { animate: !reducedMotion };
      if (points.length === 0) return;
      if (points.length === 1) {
        this.map.setView(points[0], Math.max(this.map.getZoom(), 13), options);
        return;
      }
      this.map.fitBounds(window.L.latLngBounds(points), { padding: [32, 32], maxZoom: 14, animate: !reducedMotion });
    }

    showSalon(handle) {
      const marker = this.markers.get(handle);
      this.setCurrent(handle, false);
      if (!this.map || !marker) return;
      if (!this.map.hasLayer(marker)) marker.addTo(this.map);
      this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 14), { animate: !reducedMotion });
      marker.openPopup();
      if (this.mapEl && typeof this.mapEl.scrollIntoView === 'function') {
        this.mapEl.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
      }
    }

    setCurrent(handle, scrollList) {
      this.items.forEach((item, key) => {
        if (key === handle) {
          item.setAttribute('aria-current', 'true');
          if (scrollList && typeof item.scrollIntoView === 'function') {
            item.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
          }
        } else {
          item.removeAttribute('aria-current');
        }
      });
    }

    locate() {
      if (!this.locateButton) return;
      if (!('geolocation' in navigator)) {
        this.setStatus(TEXT.noGeo);
        this.locateButton.hidden = true;
        return;
      }
      this.locateButton.disabled = true;
      this.locateButton.textContent = TEXT.locating;
      this.setStatus(TEXT.locating);

      const done = () => {
        this.locateButton.disabled = false;
        this.locateButton.textContent = TEXT.locateLabel;
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          done();
          this.userPosition = { lat: position.coords.latitude, lng: position.coords.longitude };
          this.sortByDistance();
        },
        (error) => {
          done();
          if (error && error.code === 1) this.setStatus(TEXT.denied);
          else if (error && error.code === 3) this.setStatus(TEXT.timeout);
          else this.setStatus(TEXT.unavailable);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    sortByDistance() {
      if (!this.userPosition || !this.list) return;
      const withDistance = [];
      const without = [];
      this.salons.forEach((salon) => {
        if (salon.lat == null || salon.lng == null) {
          salon.distance = null;
          without.push(salon);
          return;
        }
        salon.distance = distanceKm(this.userPosition, { lat: salon.lat, lng: salon.lng });
        withDistance.push(salon);
      });
      withDistance.sort((a, b) => a.distance - b.distance);
      this.salons = withDistance.concat(without);

      this.salons.forEach((salon) => {
        const item = this.items.get(salon.handle);
        if (!item) return;
        const distanceEl = item.querySelector('[data-store-locator-distance]');
        if (distanceEl) {
          if (salon.distance == null) {
            distanceEl.hidden = true;
            distanceEl.textContent = '';
          } else {
            distanceEl.hidden = false;
            distanceEl.textContent = formatKm(salon.distance) + ' van je locatie';
          }
        }
        this.list.appendChild(item);
      });

      if (this.map && window.L) {
        if (this.userMarker) this.map.removeLayer(this.userMarker);
        this.userMarker = window.L.circleMarker([this.userPosition.lat, this.userPosition.lng], {
          radius: 8,
          color: '#101010',
          weight: 2,
          fillColor: '#ffffff',
          fillOpacity: 1,
        }).bindTooltip('Jouw locatie');
        this.userMarker.addTo(this.map);
      }

      this.applyFilter(this.search ? this.search.value : '');
      this.setStatus(this.visibleSalons.length ? TEXT.sorted : TEXT.none);
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }
  }

  window.customElements.define('store-locator', StoreLocator);
})();
